import React from 'react';
import PropTypes from 'prop-types';
import OrderSummery from './OrderSummery';

const PreviewStep = ({
  quotationTemplate,
  properties,
  companyId,
  onOrderChange,
  selectedAssets,
  setSelectedAssets,
  termsUrl,
  termsAccepted,
  onToggleTerms,
}) => {
  const SectionCard = ({ title, children }) => (
    <div className="bg-white shadow-md rounded-lg p-6 mb-6">
      <h3 className="text-xl font-semibold mb-4 pb-2 border-b border-gray-200">{title}</h3>
      {children}
    </div>
  );

  const InfoRow = ({ label, value }) => {
    const isNA =
      value === null ||
      value === undefined ||
      (typeof value === 'string' &&
        (value.trim() === '' ||
         value.trim().toLowerCase() === 'n/a' ||
         value.toLowerCase().includes('undefined') ||
         value.toLowerCase().includes('null')));

    if (isNA) return null;

    return (
      <div className="flex flex-wrap mb-2">
        <span className="w-full sm:w-1/3 font-medium text-gray-600">{label}:</span>
        <span className="w-full sm:w-2/3">{value}</span>
      </div>
    );
  };

  const formatValue = (value) => {
    if (value === null || value === undefined) return 'N/A';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (typeof value === 'object') {
      if (value.Address) {
        // Google Place object
        return (
          <div>
            <div>{value.Address}</div>
            {value.City && <div>{value.City}, {value.State} {value.PostalCode}</div>}
            {value.Country && <div>{value.Country}</div>}
          </div>
        );
      }
      return JSON.stringify(value, null, 2);
    }
    return String(value);
  };

  const renderSection = (step) => {
    if (!step || !Array.isArray(step.fields) || step.fields.length === 0) {
      return null; // Don't render anything if fields is not a valid array or is empty
    }
    const previewFields = step.fields.filter(
      (field) => field?.showInPreview !== false
    );

    if (previewFields.length === 0) return null;
    // Build list of valid fields and group them
    const validFields = [];
    const seenGroups = new Set();
    const groupOrder = [];
    const groupMap = new Map();

    previewFields.forEach((field) => {
      const formatted = formatValue(field.value);
      if (typeof formatted === 'string') {
        const f = formatted.trim().toLowerCase();
        if (f === 'n/a' || f === '') return;
        if (f.includes('undefined') || f.includes('null')) return;
      }
      validFields.push({ field, formatted });
      const groupName = (field.group && String(field.group).trim()) || '';
      if (groupName) {
        if (!seenGroups.has(groupName)) {
          seenGroups.add(groupName);
          groupOrder.push(groupName);
        }
        if (!groupMap.has(groupName)) groupMap.set(groupName, []);
        groupMap.get(groupName).push({ field, formatted });
      }
    });

    if (validFields.length === 0) return null;

    const ungroupedRows = validFields
      .filter(({ field }) => !field.group || String(field.group).trim() === '')
      .map(({ field, formatted }) => (
        <InfoRow key={field.id} label={field.label} value={formatted} />
      ));

    return (
      <SectionCard title={step.label || step.id}>
        {ungroupedRows.length > 0 && (
          <div className="space-y-2">{ungroupedRows}</div>
        )}
        {groupOrder.map((groupName) => (
          <div
            key={`${step.id}-group-${groupName}`}
            className="mt-4 border border-gray-200 rounded-md p-4"
          >
            <h4 className="text-sm font-semibold text-gray-700 mb-3">{groupName}</h4>
            <div className="space-y-2">
              {groupMap.get(groupName).map(({ field, formatted }) => (
                <InfoRow key={field.id} label={field.label} value={formatted} />
              ))}
            </div>
          </div>
        ))}
      </SectionCard>
    );
  };

  if (!quotationTemplate || !Array.isArray(quotationTemplate.wizardSteps) || quotationTemplate.wizardSteps.length === 0) {
    return (
      <div className="space-y-6">
        <h2 className="text-2xl font-bold mb-4">Preview</h2>
        <p className="text-red-600">No data available for preview.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold mb-4">Preview</h2>
      <p className="text-gray-600 mb-6">Review your information before submitting</p>
      {/* Order Summary first, if there are items (component returns null when empty) */}
      <OrderSummery
        quotationTemplate={quotationTemplate}
        properties={properties}
        companyId={companyId}
        onOrderChange={onOrderChange}
        selectedAssets={selectedAssets}
        setSelectedAssets={setSelectedAssets}
        hideRemove={true}
      />
      
      {quotationTemplate.wizardSteps.map((step) => renderSection(step))}

      {/* Terms & Conditions */}
      <div className="bg-white shadow-md rounded-lg p-6">
        <h3 className="text-lg font-semibold mb-3">Terms & Conditions</h3>
        { termsUrl ? (
          <div className="mb-3 text-sm">
            Please read our <a href={termsUrl} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">Terms & Conditions</a>.
          </div>
        ) : (
          <div className="mb-3 text-sm text-gray-500">Please review and accept the Terms & Conditions to proceed.</div>
        )}
        <div className="flex items-start">
          <input
            id="terms"
            type="checkbox"
            checked={!!termsAccepted}
            onChange={(e) => onToggleTerms && onToggleTerms(e.target.checked)}
            className="mt-1 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
          />
          <label htmlFor="terms" className="ml-3 text-sm text-gray-700">
            I have reviewed the information and agree to the Terms & Conditions.
          </label>
        </div>
      </div>
    </div>
  );
};

PreviewStep.propTypes = {
  quotationTemplate: PropTypes.shape({
    wizardSteps: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string,
      fields: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
        value: PropTypes.any,
        group: PropTypes.string,
      })),
    })).isRequired,
  }).isRequired,
  properties: PropTypes.array,
  companyId: PropTypes.string,
  onOrderChange: PropTypes.func,
  selectedAssets: PropTypes.array,
  setSelectedAssets: PropTypes.func,
  termsContent: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  termsUrl: PropTypes.string,
  termsAccepted: PropTypes.bool,
  onToggleTerms: PropTypes.func,
};

export default PreviewStep;